import React from 'react';
import { FiLock, FiEye, FiShield, FiUserCheck, FiDatabase, FiAlertCircle } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import settings from '../config/settings';
import SEO from '../components/SEO';

const Privacy = () => {
  const lastUpdated = "February 2026";

  return (
    <div className="animate-fadeIn">
      <SEO page="privacy" />
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 pt-32 pb-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Breadcrumbs in Hero */}
          <div className="flex items-center justify-center space-x-2 text-sm text-gray-600 mb-6" data-aos="fade-down">
            <a href="/" className="hover:text-emerald-600 transition-colors">Home</a>
            <span className="text-gray-400">/</span>
            <span className="text-blue-600 font-medium">Privacy Policy</span>
          </div>

          <div className="text-center max-w-4xl mx-auto">
            <div className="w-16 h-16 bg-blue-600 rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-lg" data-aos="fade-up">
              <SafeIcon icon={FiLock} className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4" data-aos="fade-up" data-aos-delay="100">
              Privacy <span className="text-blue-600">Policy</span>
            </h1>
            <p className="text-lg text-gray-600 leading-relaxed mb-6" data-aos="fade-up" data-aos-delay="200">
              Your child's privacy and data security are our top priorities at {settings.siteName}
            </p>
            <div className="mt-6 inline-flex items-center space-x-2 bg-white px-6 py-3 rounded-full shadow-sm" data-aos="fade-up" data-aos-delay="300">
              <span className="text-sm text-gray-500">Last updated:</span>
              <span className="text-sm font-semibold text-blue-600">{lastUpdated}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Privacy Commitment Cards */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-6 mb-16">
            <div className="bg-blue-50 rounded-2xl p-6" data-aos="fade-up">
              <SafeIcon icon={FiShield} className="w-12 h-12 text-blue-600 mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-2">Data Protection</h3>
              <p className="text-gray-600">We employ industry-standard security measures to protect your information.</p>
            </div>
            <div className="bg-purple-50 rounded-2xl p-6" data-aos="fade-up" data-aos-delay="100">
              <SafeIcon icon={FiUserCheck} className="w-12 h-12 text-purple-600 mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-2">Limited Access</h3>
              <p className="text-gray-600">Only authorized staff have access to sensitive student information.</p>
            </div>
            <div className="bg-indigo-50 rounded-2xl p-6" data-aos="fade-up" data-aos-delay="200">
              <SafeIcon icon={FiEye} className="w-12 h-12 text-indigo-600 mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-2">Transparency</h3>
              <p className="text-gray-600">We're clear about what data we collect and how we use it.</p>
            </div>
          </div>

          {/* Privacy Content */}
          <div className="max-w-4xl mx-auto">
            {/* Introduction */}
            <div className="mb-12" data-aos="fade-up">
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Our Commitment to Privacy</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  At {settings.siteName}, we understand the importance of protecting the personal information of our students and their families. This Privacy Policy explains how we collect, use, store, and protect your data in compliance with applicable data protection laws.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  By enrolling your child at our school, you consent to the data practices described in this policy. We encourage you to read this policy carefully and contact us if you have any questions.
                </p>
              </div>
            </div>

            {/* Section 1 */}
            <div className="mb-12" data-aos="fade-up">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center">
                <span className="w-10 h-10 bg-blue-600 text-white rounded-xl flex items-center justify-center text-lg font-bold mr-4">1</span>
                Information We Collect
              </h2>
              <div className="bg-gray-50 rounded-2xl p-8 space-y-6">
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">1.1 Student Information</h3>
                  <ul className="space-y-3 text-gray-700">
                    <li className="flex items-start">
                      <span className="text-blue-600 mr-3 mt-1 font-bold">•</span>
                      <span><strong>Personal Details:</strong> Full name, date of birth, gender, birth certificate, photographs</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-blue-600 mr-3 mt-1 font-bold">•</span>
                      <span><strong>Medical Information:</strong> Health conditions, allergies, immunization records, medication requirements</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-blue-600 mr-3 mt-1 font-bold">•</span>
                      <span><strong>Academic Records:</strong> Progress reports, assessments, attendance records, behavioral observations</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-blue-600 mr-3 mt-1 font-bold">•</span>
                      <span><strong>Special Needs:</strong> Learning requirements, dietary restrictions, support services needed</span>
                    </li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">1.2 Parent/Guardian Information</h3>
                  <ul className="space-y-3 text-gray-700">
                    <li className="flex items-start">
                      <span className="text-blue-600 mr-3 mt-1 font-bold">•</span>
                      <span><strong>Contact Details:</strong> Names, addresses, phone numbers, email addresses</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-blue-600 mr-3 mt-1 font-bold">•</span>
                      <span><strong>Emergency Contacts:</strong> Authorized persons for pick-up, emergency contact information</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-blue-600 mr-3 mt-1 font-bold">•</span>
                      <span><strong>Financial Information:</strong> Billing details, payment methods (processed securely)</span>
                    </li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">1.3 Automatically Collected Data</h3>
                  <ul className="space-y-3 text-gray-700">
                    <li className="flex items-start">
                      <span className="text-blue-600 mr-3 mt-1 font-bold">•</span>
                      <span><strong>Website Analytics:</strong> IP addresses, browser type, device information, pages visited</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-blue-600 mr-3 mt-1 font-bold">•</span>
                      <span><strong>Cookies:</strong> Session data to improve website functionality and user experience</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-blue-600 mr-3 mt-1 font-bold">•</span>
                      <span><strong>CCTV Footage:</strong> Security cameras operating in common areas (not in restrooms/changing areas)</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Section 2 */}
            <div className="mb-12" data-aos="fade-up">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center">
                <span className="w-10 h-10 bg-blue-600 text-white rounded-xl flex items-center justify-center text-lg font-bold mr-4">2</span>
                How We Use Your Information
              </h2>
              <div className="bg-gray-50 rounded-2xl p-8 space-y-6">
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">2.1 Educational Purposes</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start">
                      <span className="text-blue-600 mr-2 mt-1">✓</span>
                      <span>Managing student enrollment and attendance</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-blue-600 mr-2 mt-1">✓</span>
                      <span>Delivering quality education and tracking academic progress</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-blue-600 mr-2 mt-1">✓</span>
                      <span>Providing personalized learning experiences</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-blue-600 mr-2 mt-1">✓</span>
                      <span>Communicating with parents about their child's development</span>
                    </li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">2.2 Health & Safety</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start">
                      <span className="text-blue-600 mr-2 mt-1">✓</span>
                      <span>Ensuring child safety and well-being</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-blue-600 mr-2 mt-1">✓</span>
                      <span>Managing medical emergencies and administering necessary care</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-blue-600 mr-2 mt-1">✓</span>
                      <span>Accommodating dietary and medical requirements</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-blue-600 mr-2 mt-1">✓</span>
                      <span>Maintaining security through CCTV monitoring</span>
                    </li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">2.3 Administrative Functions</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start">
                      <span className="text-blue-600 mr-2 mt-1">✓</span>
                      <span>Processing fee payments and maintaining financial records</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-blue-600 mr-2 mt-1">✓</span>
                      <span>Sending newsletters, updates, and important announcements</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-blue-600 mr-2 mt-1">✓</span>
                      <span>Complying with legal and regulatory requirements</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-blue-600 mr-2 mt-1">✓</span>
                      <span>Improving our services and facilities</span>
                    </li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">2.4 Marketing & Promotional</h3>
                  <p className="text-gray-700 leading-relaxed mb-3">
                    With your consent, we may use photos and videos for:
                  </p>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start">
                      <span className="text-blue-600 mr-2 mt-1">✓</span>
                      <span>School website, social media, and promotional materials</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-blue-600 mr-2 mt-1">✓</span>
                      <span>Internal newsletters and yearbooks</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-blue-600 mr-2 mt-1">✓</span>
                      <span>Educational presentations and workshops</span>
                    </li>
                  </ul>
                  <p className="text-sm text-gray-600 mt-3 italic">
                    Note: You may opt out of photo/video usage at any time by notifying us in writing.
                  </p>
                </div>
              </div>
            </div>

            {/* Section 3 */}
            <div className="mb-12" data-aos="fade-up">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center">
                <span className="w-10 h-10 bg-blue-600 text-white rounded-xl flex items-center justify-center text-lg font-bold mr-4">3</span>
                Data Sharing & Disclosure
              </h2>
              <div className="bg-gray-50 rounded-2xl p-8 space-y-6">
                <div className="bg-amber-50 border-l-4 border-amber-500 p-6 rounded-r-xl">
                  <div className="flex items-start">
                    <SafeIcon icon={FiAlertCircle} className="w-6 h-6 text-amber-600 mr-3 flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="font-bold text-gray-900 mb-2">Important:</h4>
                      <p className="text-gray-700 text-sm">
                        We do not sell, rent, or trade your personal information to third parties for marketing purposes.
                      </p>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">We May Share Information With:</h3>
                  <ul className="space-y-3 text-gray-700">
                    <li className="flex items-start">
                      <span className="text-blue-600 mr-3 mt-1 font-bold">•</span>
                      <span><strong>Educational Staff:</strong> Teachers and administrators who need access to provide services</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-blue-600 mr-3 mt-1 font-bold">•</span>
                      <span><strong>Service Providers:</strong> Payment processors, IT support, security services (under strict confidentiality agreements)</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-blue-600 mr-3 mt-1 font-bold">•</span>
                      <span><strong>Legal Authorities:</strong> When required by law, court order, or to protect child safety</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-blue-600 mr-3 mt-1 font-bold">•</span>
                      <span><strong>Medical Professionals:</strong> In case of emergencies or when parents have provided consent</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-blue-600 mr-3 mt-1 font-bold">•</span>
                      <span><strong>Regulatory Bodies:</strong> Education authorities and accreditation organizations as required</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Section 4 */}
            <div className="mb-12" data-aos="fade-up">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center">
                <span className="w-10 h-10 bg-blue-600 text-white rounded-xl flex items-center justify-center text-lg font-bold mr-4">4</span>
                Data Security Measures
              </h2>
              <div className="bg-gray-50 rounded-2xl p-8 space-y-6">
                <p className="text-gray-700 leading-relaxed">
                  We implement comprehensive security measures to protect your information from unauthorized access, alteration, disclosure, or destruction:
                </p>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="bg-white rounded-xl p-6 border border-gray-200">
                    <div className="flex items-center mb-3">
                      <SafeIcon icon={FiLock} className="w-6 h-6 text-blue-600 mr-3" />
                      <h4 className="font-bold text-gray-900">Physical Security</h4>
                    </div>
                    <ul className="space-y-2 text-sm text-gray-600">
                      <li>• Locked storage for physical documents</li>
                      <li>• Controlled access to campus facilities</li>
                      <li>• CCTV surveillance in common areas</li>
                    </ul>
                  </div>

                  <div className="bg-white rounded-xl p-6 border border-gray-200">
                    <div className="flex items-center mb-3">
                      <SafeIcon icon={FiDatabase} className="w-6 h-6 text-blue-600 mr-3" />
                      <h4 className="font-bold text-gray-900">Digital Security</h4>
                    </div>
                    <ul className="space-y-2 text-sm text-gray-600">
                      <li>• Encrypted data storage and transmission</li>
                      <li>• Secure, password-protected systems</li>
                      <li>• Regular security audits and updates</li>
                    </ul>
                  </div>

                  <div className="bg-white rounded-xl p-6 border border-gray-200">
                    <div className="flex items-center mb-3">
                      <SafeIcon icon={FiUserCheck} className="w-6 h-6 text-blue-600 mr-3" />
                      <h4 className="font-bold text-gray-900">Access Control</h4>
                    </div>
                    <ul className="space-y-2 text-sm text-gray-600">
                      <li>• Limited access based on job role</li>
                      <li>• Staff training on data protection</li>
                      <li>• Regular access reviews and audits</li>
                    </ul>
                  </div>

                  <div className="bg-white rounded-xl p-6 border border-gray-200">
                    <div className="flex items-center mb-3">
                      <SafeIcon icon={FiShield} className="w-6 h-6 text-blue-600 mr-3" />
                      <h4 className="font-bold text-gray-900">Backup & Recovery</h4>
                    </div>
                    <ul className="space-y-2 text-sm text-gray-600">
                      <li>• Regular automated backups</li>
                      <li>• Disaster recovery procedures</li>
                      <li>• Secure off-site data storage</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Section 5 */}
            <div className="mb-12" data-aos="fade-up">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center">
                <span className="w-10 h-10 bg-blue-600 text-white rounded-xl flex items-center justify-center text-lg font-bold mr-4">5</span>
                Your Privacy Rights
              </h2>
              <div className="bg-gray-50 rounded-2xl p-8 space-y-6">
                <p className="text-gray-700 leading-relaxed mb-4">
                  You have the following rights regarding your personal information:
                </p>

                <div className="space-y-4">
                  <div className="bg-white rounded-lg p-5 border-l-4 border-blue-600">
                    <h4 className="font-bold text-gray-900 mb-2">Right to Access</h4>
                    <p className="text-gray-600 text-sm">Request a copy of the personal information we hold about you or your child.</p>
                  </div>

                  <div className="bg-white rounded-lg p-5 border-l-4 border-purple-600">
                    <h4 className="font-bold text-gray-900 mb-2">Right to Correction</h4>
                    <p className="text-gray-600 text-sm">Request correction of inaccurate or incomplete information.</p>
                  </div>

                  <div className="bg-white rounded-lg p-5 border-l-4 border-indigo-600">
                    <h4 className="font-bold text-gray-900 mb-2">Right to Deletion</h4>
                    <p className="text-gray-600 text-sm">Request deletion of personal information (subject to legal retention requirements).</p>
                  </div>

                  <div className="bg-white rounded-lg p-5 border-l-4 border-cyan-600">
                    <h4 className="font-bold text-gray-900 mb-2">Right to Restrict Processing</h4>
                    <p className="text-gray-600 text-sm">Request limitation on how we use your information.</p>
                  </div>

                  <div className="bg-white rounded-lg p-5 border-l-4 border-teal-600">
                    <h4 className="font-bold text-gray-900 mb-2">Right to Withdraw Consent</h4>
                    <p className="text-gray-600 text-sm">Withdraw consent for specific uses, such as marketing communications or photography.</p>
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mt-6">
                  <p className="text-sm text-blue-900">
                    <strong>To exercise your rights,</strong> contact us at <a href={`mailto:${settings.email}`} className="underline font-semibold">{settings.email}</a> or call {settings.phones[0].display}. We will respond within 30 days.
                  </p>
                </div>
              </div>
            </div>

            {/* Section 6 */}
            <div className="mb-12" data-aos="fade-up">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center">
                <span className="w-10 h-10 bg-blue-600 text-white rounded-xl flex items-center justify-center text-lg font-bold mr-4">6</span>
                Data Retention
              </h2>
              <div className="bg-gray-50 rounded-2xl p-8 space-y-4">
                <p className="text-gray-700 leading-relaxed">
                  We retain personal information for as long as necessary to fulfill the purposes outlined in this policy and to comply with legal obligations:
                </p>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start">
                    <span className="text-blue-600 mr-3 mt-1 font-bold">•</span>
                    <span><strong>Academic Records:</strong> Retained for a minimum of 7 years after graduation/withdrawal</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-600 mr-3 mt-1 font-bold">•</span>
                    <span><strong>Financial Records:</strong> Maintained for 7 years as per tax regulations</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-600 mr-3 mt-1 font-bold">•</span>
                    <span><strong>Medical Records:</strong> Kept securely throughout enrollment and for 3 years after departure</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-600 mr-3 mt-1 font-bold">•</span>
                    <span><strong>CCTV Footage:</strong> Stored for 30-90 days unless required for investigations</span>
                  </li>
                </ul>
                <p className="text-gray-600 text-sm italic mt-4">
                  After the retention period, information is securely destroyed or permanently anonymized.
                </p>
              </div>
            </div>

            {/* Section 7 */}
            <div className="mb-12" data-aos="fade-up">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center">
                <span className="w-10 h-10 bg-blue-600 text-white rounded-xl flex items-center justify-center text-lg font-bold mr-4">7</span>
                Cookies & Website Analytics
              </h2>
              <div className="bg-gray-50 rounded-2xl p-8 space-y-4">
                <p className="text-gray-700 leading-relaxed">
                  Our website uses cookies and similar technologies to enhance your browsing experience and understand how visitors interact with our site.
                </p>
                <div>
                  <h4 className="font-bold text-gray-900 mb-3">Types of Cookies We Use:</h4>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start">
                      <span className="text-blue-600 mr-2 mt-1">•</span>
                      <span><strong>Essential Cookies:</strong> Required for website functionality</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-blue-600 mr-2 mt-1">•</span>
                      <span><strong>Analytics Cookies:</strong> Help us understand visitor behavior and improve our site</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-blue-600 mr-2 mt-1">•</span>
                      <span><strong>Functional Cookies:</strong> Remember your preferences and settings</span>
                    </li>
                  </ul>
                </div>
                <p className="text-gray-600 text-sm">
                  You can control cookies through your browser settings. Please note that disabling cookies may affect website functionality.
                </p>
              </div>
            </div>

            {/* Section 8 */}
            <div className="mb-12" data-aos="fade-up">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center">
                <span className="w-10 h-10 bg-blue-600 text-white rounded-xl flex items-center justify-center text-lg font-bold mr-4">8</span>
                Children's Privacy Protection
              </h2>
              <div className="bg-gray-50 rounded-2xl p-8 space-y-4">
                <p className="text-gray-700 leading-relaxed">
                  We are committed to protecting the privacy of children. Our practices align with child protection laws and regulations:
                </p>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start">
                    <span className="text-blue-600 mr-3 mt-1 font-bold">•</span>
                    <span>We collect only information necessary for educational purposes and child safety</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-600 mr-3 mt-1 font-bold">•</span>
                    <span>We require parental consent before collecting any personal information about children</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-600 mr-3 mt-1 font-bold">•</span>
                    <span>Children's information is stored securely and accessed only by authorized staff</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-600 mr-3 mt-1 font-bold">•</span>
                    <span>We do not knowingly disclose children's information without parental consent</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-600 mr-3 mt-1 font-bold">•</span>
                    <span>Parents have the right to review, update, or delete their child's information</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Section 9 */}
            <div className="mb-12" data-aos="fade-up">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center">
                <span className="w-10 h-10 bg-blue-600 text-white rounded-xl flex items-center justify-center text-lg font-bold mr-4">9</span>
                Third-Party Links
              </h2>
              <div className="bg-gray-50 rounded-2xl p-8 space-y-4">
                <p className="text-gray-700 leading-relaxed">
                  Our website may contain links to third-party websites for educational resources or additional information. Please note:
                </p>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <span className="text-blue-600 mr-2 mt-1">•</span>
                    <span>We are not responsible for the privacy practices of external websites</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-600 mr-2 mt-1">•</span>
                    <span>We encourage you to review the privacy policies of any sites you visit</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-600 mr-2 mt-1">•</span>
                    <span>This privacy policy applies only to information collected by {settings.siteName}</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Section 10 */}
            <div className="mb-12" data-aos="fade-up">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center">
                <span className="w-10 h-10 bg-blue-600 text-white rounded-xl flex items-center justify-center text-lg font-bold mr-4">10</span>
                Changes to This Policy
              </h2>
              <div className="bg-gray-50 rounded-2xl p-8 space-y-4">
                <p className="text-gray-700 leading-relaxed">
                  We may update this Privacy Policy periodically to reflect changes in our practices, technology, or legal requirements. When we make significant changes:
                </p>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <span className="text-blue-600 mr-2 mt-1">•</span>
                    <span>We will update the "Last Updated" date at the top of this page</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-600 mr-2 mt-1">•</span>
                    <span>We will notify parents via email or in-person communication</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-600 mr-2 mt-1">•</span>
                    <span>We encourage parents to review this policy regularly</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Contact Section */}
            <div className="bg-gradient-to-br from-blue-600 to-indigo-600 rounded-3xl p-10 text-center text-white" data-aos="fade-up">
              <h3 className="text-2xl font-bold mb-4">Questions About Our Privacy Policy?</h3>
              <p className="text-blue-50 mb-6 max-w-2xl mx-auto">
                If you have any questions or need clarification regarding our privacy practices, please don't hesitate to contact us.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="/contact"
                  className="bg-white text-blue-600 px-8 py-3 rounded-full font-semibold hover:bg-blue-50 transition-colors"
                >
                  Contact Us
                </a>
                <a
                  href={`mailto:${settings.email}`}
                  className="bg-blue-700 text-white px-8 py-3 rounded-full font-semibold hover:bg-blue-800 transition-colors"
                >
                  Email: {settings.email}
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Privacy;
